class TabbedContent extends HTMLElement {
  constructor() {
    super();
    this.videoLoadState = new Map(); // Track which videos are loaded
    this.intersectionObserver = null;
    this.sectionLoaded = false;
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('[slot="tabs"] button[role="tab"]');
    this.panels = this.querySelectorAll('[slot="panels"] section[role="tabpanel"]');
    const controlsPrestigeCard = this.hasAttribute('data-controls-prestige-card');
    const sectionId = this.getAttribute('data-section-id');

    // Setup intersection observer for below-the-fold loading
    if (sectionId) {
      this.setupIntersectionObserver(sectionId);
    }

    // Handle tab switching (no need to set initial attributes - already in HTML)
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        this.activateTab(tab);

        if (controlsPrestigeCard && sectionId) {
          window.requestAnimationFrame(() => {
            this.updatePrestigeCard(tab, sectionId);
            this.updateVideo(tab, sectionId);
          });
        }
      });
      tab.addEventListener("keydown", (e) => this.handleKeydown(e, this.tabs));
    });
  }

  setupIntersectionObserver(sectionId) {
    const section = document.querySelector(`#dynamic-video-${sectionId}`)?.closest('.shopify-section');
    if (!section) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.sectionLoaded) {
            this.sectionLoaded = true;
            this.extractAllVideosFromTemplates(sectionId);
          }
        });
      },
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before entering viewport
        threshold: 0.1
      }
    );

    this.intersectionObserver.observe(section);
  }

  extractAllVideosFromTemplates(sectionId) {
    const videoContainer = document.querySelector(`#dynamic-video-${sectionId}`);
    if (!videoContainer) return;

    // Extract all videos from templates
    const templates = document.querySelectorAll(`template[id^="tier-background-"]`);
    templates.forEach(template => {
      const clonedContent = template.content.cloneNode(true);
      const videoWrapper = clonedContent.querySelector('div');

      videoWrapper.classList.add('opacity-0');
      videoWrapper.style.opacity = '0';
      videoContainer.appendChild(videoWrapper);
    });
  }


  loadVideo(videoElement, tier) {
    if (!videoElement) return;

    if (this.videoLoadState.get(tier) === 'loaded') {
      return; // Already loaded
    }

    this.videoLoadState.set(tier, 'loading');

    // Load the video
    videoElement.load();

    const handleCanPlay = () => {
      this.videoLoadState.set(tier, 'loaded');
      videoElement.play();
    };

    const handleError = () => {
      this.videoLoadState.set(tier, 'error');
      console.warn(`Failed to load video for tier: ${tier}`);
    };

    videoElement.addEventListener('canplay', handleCanPlay, { once: true });
    videoElement.addEventListener('error', handleError, { once: true });
  }

  updatePrestigeCard(selectedTab, sectionId) {
    const tier = selectedTab.getAttribute('data-tier');
    if (!tier) return;

    const card = document.querySelector(`#dynamic-prestige-card-${sectionId} .prestige-membership-card[tier]`);
    if (!card) return;

    const logo = document.querySelector(`#dynamic-prestige-card-${sectionId} r-icon-lottie[tier]`);
    if (!logo) return;

    // Update the attribute to update the styling
    card.setAttribute('tier', tier);
    logo.setAttribute('tier', tier);
    // Update the tier text if present
    const tierText = card.querySelector('.prestige-membership-card__tier');
    if (tier === 'vip') {
      tierText.classList.add('uppercase');
      tierText.classList.remove('capitalize');
    } else {
      tierText.classList.add('capitalize');
      tierText.classList.remove('uppercase');
    }
    if (tierText) {
      tierText.textContent = tier;
    }
  }

  updateVideo(selectedTab, sectionId) {
    const tier = selectedTab.getAttribute('data-tier');
    if (!tier) return;

    const videoContainer = document.querySelector(`#dynamic-video-${sectionId}`);
    if (!videoContainer) return;

    // Get all video wrappers (divs containing videos)
    const videoWrappers = videoContainer.querySelectorAll('[data-tier-video-background-wrapper]');
    const targetVideo = videoContainer.querySelector(`video[data-tier="${tier}"]`);

    if (!targetVideo) return;

    const targetWrapper = targetVideo.closest('[data-tier-video-background-wrapper]');

    // Hide all videos with fade transition
    videoWrappers.forEach(wrapper => {
      if (!wrapper.classList.contains('opacity-0')) {
        wrapper.style.opacity = '0';
        setTimeout(() => {
          wrapper.classList.add('opacity-0');
          const video = wrapper.querySelector('video');
          if (video) video.pause();
        }, 150);
      }
    });

    // Show target video
    if (targetWrapper) {
      const video = targetWrapper.querySelector('video');

      // Load video if not already loaded
      if (!this.videoLoadState.get(tier)) {
        this.loadVideo(video, tier);
      }

      // Show wrapper and fade in
      targetWrapper.classList.remove('opacity-0');
      targetWrapper.style.opacity = '1';

      // Play video if loaded
      if (this.videoLoadState.get(tier) === 'loaded') {
        video.play();
        return;
      }

      // Wait for video to load
      const handleCanPlay = () => {
        video.play();
      };
      video.addEventListener('canplay', handleCanPlay, { once: true });
    }
  }

  activateTab(selectedTab) {
    if (!(selectedTab instanceof HTMLElement)) {
      selectedTab = Array.from(this.tabs).find(tab => tab.id === selectedTab);
    };

    this.tabs.forEach((tab) => {
      const isActive = tab === selectedTab;
      tab.setAttribute("aria-selected", isActive);
      tab.tabIndex = isActive ? 0 : -1;
      tab.classList.toggle("active", isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.id === selectedTab.getAttribute("aria-controls");
      panel.classList.toggle("active", isActive);
      panel.classList.toggle("z-10", isActive);
      panel.classList.toggle("opacity-0", !isActive);
      panel.classList.toggle("pointer-events-none", !isActive);
    });
  }

  handleKeydown(e, tabs) {
    const currentIndex = [...tabs].indexOf(document.activeElement);
    let newIndex = null;

    if (e.key === "ArrowRight") newIndex = (currentIndex + 1) % tabs.length;
    if (e.key === "ArrowLeft") newIndex = (currentIndex - 1 + tabs.length) % tabs.length;

    if (newIndex !== null) {
      tabs[newIndex].focus();
      tabs[newIndex].click();
    }
  }
}

customElements.define("tabbed-content", TabbedContent);